import * as React from 'react'
import styled from 'styled-components'
import { H2, P } from './shared'
import { OutlineOnFocusVisible } from './shared/buttons'
import { colors } from '@components/shared/colors'
import { config } from 'react-awesome-styled-grid'
import { SoundWaveAnimation } from './shared/SoundWaveAnimation'

export type TrackClipSlideType = {
  img: string,
  title: string,
  tags: string,
  mp3Url: string,
  startTime: number,
  endTime: number,
  duration: number,
  killAudio?: boolean
}

type PropsType = TrackClipSlideType & {
  audioIndex: number,
  currentAudio: number,
  setCurrentAudio: () => void
}
type StateType = {
  isPlaying: boolean,
  hasPlayed: boolean,
  currentTime: string,
  currentTimeRaw: number,
  startTime: string,
  startTimeRaw: number,
  endTime: string,
  endTimeRaw: number
}
// https://firebasestorage.googleapis.com/v0/b/dipsea-images-resized/o/500-384_OfftheRecordI_SuzanneDias-fs8.png?alt=media&token=42911f2e-2887-4c66-94df-5253238c7596
export default class TrackClip extends React.PureComponent<
  PropsType,
  StateType
> {
  _audio: any
  _isMounted: boolean = false
  _handlerProgress: any
  _interval: any

  constructor (props: PropsType) {
    super(props)
    this.state = {
      isPlaying: false,
      hasPlayed: false,
      currentTime: this.formatTime(props.startTime),
      currentTimeRaw: 0,
      startTime: this.formatTime(props.startTime),
      startTimeRaw: props.startTime,
      endTime: this.formatTime(props.endTime),
      endTimeRaw: props.endTime
    }
    this._handlerProgress = (): void => this.onProgress()
  }

  componentDidMount = (): void => {
    this._audio = new window.Audio(this.props.mp3Url)
    this._isMounted = true
  }

  componentWillUnmount = (): void => {
    this._isMounted = false
  }
  // eslint-disable-next-line
  UNSAFE_componentWillUpdate = (nextProps: PropsType, nextState: StateType): void => {
    if (nextProps.killAudio && this.props.audioIndex === this.props.currentAudio) {
      this.pauseAudio()
    } else if (this.state.isPlaying && nextProps.audioIndex !== nextProps.currentAudio) {
      this.pauseAudio()
    }
  }

  formatTime (time: number | null | undefined): string {
    const timeInSeconds: number = Math.round(time || 0)
    if (timeInSeconds && timeInSeconds > 0) {
      const minutes: number = Math.floor(timeInSeconds / 60)
      const seconds = timeInSeconds - minutes * 60
      if (seconds < 10) {
        return minutes + ':0' + seconds
      }
      return minutes + ':' + seconds
    }
    return '0:00'
  }

  toggleAudio = (): void => {
    if (this.state.isPlaying) {
      this.pauseAudio()
    } else {
      this.playAudio()
    }

    this._audio.addEventListener('ended', (): void => {
      if (this._isMounted) {
        setTimeout((): void => {
          this.setState({
            isPlaying: false,
            hasPlayed: false,
            currentTime: this.formatTime(this.props.startTime),
            currentTimeRaw: 0,
            endTime: this.formatTime(this.props.endTime),
            endTimeRaw: this.props.endTime
          })
        }, 1000)
      }
    })
  }

  playAudio = (): void => {
    if (!this.state.isPlaying) {
      const playPromise = this._audio.play()
      if (playPromise !== undefined) {
        playPromise
          .then((): void => {
            this.setState({ isPlaying: true })
            this.props.setCurrentAudio()
            this._interval = setInterval(this._handlerProgress, 100)
          })
          .catch((error: any): void => {
            console.log(error)
          })
      }
    }
  }

  pauseAudio = (): void => {
    if (this.state.isPlaying) {
      this._audio.pause()
      this.setState({ isPlaying: false })
      clearInterval(this._interval)
    }
  }

  onProgress = (): void => {
    if (this.state.isPlaying) {
      this.setState({
        currentTime: this.formatTime(this._audio.currentTime + this.props.startTime),
        currentTimeRaw: this._audio.currentTime,
        endTime: this.formatTime(this.props.endTime - this._audio.currentTime),
        endTimeRaw: this.props.endTime - this._audio.currentTime
      })
    }
  }

  render (): React.ReactNode {
    return (
      <TrackPreviewOutline>
        <Frost />
        <CardHeader>PREVIEW NOW</CardHeader>

        <PlayButton
          onClick={this.toggleAudio}
          aria-label={`${this.state.isPlaying ? 'Pause' : 'Play'} track`}
        >
          <TrackArtContainer>
            <TrackPreviewImg
              alt={this.props.title}
              src={`/images/audio-slider/${this.props.img}@1x.png`}
              srcSet={`/images/audio-slider/${this.props.img}@1x.png 1x, /images/audio-slider/${this.props.img}@2x.png 2x, /images/audio-slider/${this.props.img}@3x.png 3x`}
            />
            <SvgWrapper>
              <PlayPauseWrapper
                className={`play-btn ${
                  this.state.isPlaying ? 'is-playing' : 'is-paused'
                }`}
              >
                <svg
                  width="34"
                  height="38"
                  viewBox="0 0 34 38"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M31.9885 16.3074C33.7714 17.3368 33.7714 19.9102 31.9885 20.9395L4.57635 36.7659C2.79345 37.7953 0.564814 36.5086 0.564814 34.4499L0.564815 2.79707C0.564815 0.738346 2.79345 -0.548358 4.57635 0.481001L31.9885 16.3074Z"
                    fill={colors.white}
                  />
                </svg>
              </PlayPauseWrapper>
              <PlayPauseWrapper
                className={`pause-btn ${
                  this.state.isPlaying ? 'is-playing' : 'is-paused'
                }`}
              >
                <div className="pause-bar"></div>
                <div className="pause-bar"></div>
              </PlayPauseWrapper>
            </SvgWrapper>
          </TrackArtContainer>
        </PlayButton>

        <SoundWaveAnimation isActive={this.state.isPlaying}/>

        <CardTrackTitle>{this.props.title}</CardTrackTitle>

        <CardTagText>{this.props.tags}</CardTagText>

        <AudioDiv>
          <AudioScrubber progress={(this.state.currentTimeRaw / this.props.duration) > 1 ? 1 : (this.state.currentTimeRaw / this.props.duration)} />
        </AudioDiv>
        <AudioText>
          <span>{this.state.currentTime}</span>
          <span>{this.state.endTime}</span>
        </AudioText>
      </TrackPreviewOutline>
    )
  }
}

const Frost = styled.div<any>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("/images/frost-magenta.png") no-repeat;
  background-size: cover;
  z-index: -1;
  opacity: 0.6;
`
const SvgWrapper = styled.div<any>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
  outline: none;
  border: 0;
  background: none;
  cursor: pointer;
  width: 47.25px;
  height: 47.25px;
  ${(props: any): any => config(props).media.lg`
    width: 65px;
    height: 65px;
  `}
`
const PlayPauseWrapper = styled.div<any>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  &.play-btn {
    margin: 0 0 0 13%;
    will-change: transform, opacity;
    transform: scale(1) translateZ(0);
    opacity: 1;
    transition: transform 0.2s ease-in, opacity 0.2s ease-in;
    filter: drop-shadow(1px 1px 8px rgba(0, 0, 0, 0.5));
    &.is-playing {
      transform: scale(0.5) translateZ(0);
      opacity: 0;
      transition-delay: 0.1s;
    }
    svg {
      width: 100%;
      height: 100%;
    }
  }
  &.pause-btn {
    will-change: transform, opacity;
    transform: scale(0.5) translateZ(0);
    opacity: 0;
    transition: transform 0.2s ease-in, opacity 0.2s ease-in;
    filter: drop-shadow(1px 1px 5px rgba(0, 0, 0, 0.5));
    &.is-playing {
      transform: scale(0.9) translateZ(0);
      opacity: 1;
      transition-delay: 0.1s;
    }
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    .pause-bar {
      background: ${colors.white};
      width: 12px;
      height: 100%;
      border-radius: 2.67px;
      &:first-child {
        margin-right: 10px;
      }
      ${(props: any): any => config(props).media.lg`
        width: 16px;
      `}
    }
  }
`
const CardHeader = styled(P)<any>`
  color: ${colors.sand};
  font-family: "Good Sans Medium";
  font-size: 12px;
  letter-spacing: 0.03em;
  line-height: 18px;
  margin: 0 0 10px;
  text-align: center;
  ${(props: any): any => config(props).media.lg`
    font-size: 14px;
    line-height: 17px;
      margin: 0 0 20px;
  `}
`
const CardTagText = styled(P)<any>`
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin: 0 0 20px;
  color: ${colors.sand};
  text-align: center;

  ${(props: any): any => config(props).media.lg`
     margin: 0 0 25px;
  `}
`

const CardTrackTitle = styled(H2)<any>`
  color: ${colors.sand};
  font-size: 24px;
  line-height: 1em;
  margin: 0 -15px 8px;
  text-align: center;

  ${(props: any): any => config(props).media.lg`
    margin: 0 -25px 16px;
    font-size: 25px;
  `}
`
const TrackArtContainer = styled.div<any>`
  position: relative;
  max-height: 157px;
  max-width: 157px;
  height: 100%;
  width: 100%;
  ${(props: any): any => config(props).media.lg`
    max-height:244px;
    max-width: 244px;
  `}
`
const PlayButton = styled.button<any>`
  outline: none;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
  margin: 0 0 15px;
  ${(props: any): any => config(props).media.lg`
    margin: 0 0 20px;
  `}
  ${OutlineOnFocusVisible}
`
const TrackPreviewImg = styled.img<any>`
  height: 100%;
  width: 100%;
`
const TrackPreviewOutline = styled.div<any>`
  position: relative;
  overflow: hidden;
  border: 1px solid ${colors.sand};
  border-radius: 10px;
  padding: 15px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props: any): any => config(props).media.lg`
    padding: 30px 37px;
  `}
`
const AudioText = styled(P)<any>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  line-height: 12px;
  color: ${colors.sand};
`
const AudioDiv = styled.div<any>`
  width: 100%;
  height: 1px;
  background ${colors.sand};
  margin-bottom: 11px;
  display: flex;
  align-items: center;
`
const AudioScrubber = styled.div<any>`
  position: relative;
  margin-left: 30px;
  height: 3px;
  width: 100px;
  background: white;

  &:after {
    top: 1.5px;
    content: "";
    will-change: transform;
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 100%;
    background-color: ${colors.orange};
    border: 3px solid white;
    transform: translate3d(
      calc(
        ${(props: any): string =>
          props.progress ? `${100 * props.progress}px` : '0px'}
      ),
      -50%,
      0
    );
  }
`
